import { ReviewingParty } from "@redotech/congo-enums/reviewing-party";
import { ReviewingPartyReason } from "@redotech/congo-enums/reviewing-party-reason";
import { ReturnStatus } from "./return-status";
import { ReturnTypeEnum } from "./return-type-enum";

export interface ReviewingPartyInput {
  returnType: ReturnTypeEnum;
  packageProtectionPlusEnabled: boolean;
  anyOrderPackageProtected: boolean;
  /** A shipment on one of the claim's orders was recorded with the insurer. */
  anyShipmentInsured: boolean;
  wrongProduct: boolean;
  /** Merchant chose to create the claim without PP+ documentation. */
  merchantSkippedDocumentation: boolean;
}

/**
 * Single source of the managed-claim / item-value decision. REDO means Redo
 * covers the item value (a PP+ managed claim); MERCHANT covers it otherwise.
 */
export function computeReviewingParty(input: ReviewingPartyInput): {
  reviewingParty: ReviewingParty;
  reason: ReviewingPartyReason;
} {
  if (input.returnType !== ReturnTypeEnum.CLAIM) {
    return {
      reviewingParty: ReviewingParty.Merchant,
      reason: ReviewingPartyReason.MerchantNotAClaim,
    };
  }
  // Insurance already bought for a shipment keeps its claims Redo-managed
  // after the merchant turns PP+ off.
  if (!input.packageProtectionPlusEnabled && !input.anyShipmentInsured) {
    return {
      reviewingParty: ReviewingParty.Merchant,
      reason: ReviewingPartyReason.MerchantPpPlusNotEnabled,
    };
  }
  if (!input.anyOrderPackageProtected) {
    return {
      reviewingParty: ReviewingParty.Merchant,
      reason: ReviewingPartyReason.MerchantNotPackageProtected,
    };
  }
  if (input.wrongProduct) {
    return {
      reviewingParty: ReviewingParty.Merchant,
      reason: ReviewingPartyReason.MerchantWrongProduct,
    };
  }
  if (input.merchantSkippedDocumentation) {
    return {
      reviewingParty: ReviewingParty.Merchant,
      reason: ReviewingPartyReason.MerchantSkippedPpPlusDocumentation,
    };
  }
  return {
    reviewingParty: ReviewingParty.Redo,
    reason: ReviewingPartyReason.RedoCoveredByPpPlus,
  };
}

export function hasInsuredShipment(
  orders: readonly { recordedShipments?: readonly unknown[] | null }[],
): boolean {
  return orders.some((order) => !!order.recordedShipments?.length);
}

/**
 * A Redo-owned (PP+) claim awaiting Redo's review — only the claim automation
 * or Redo admins may act on it. Once approved (OPEN), merchants may process.
 */
export function isClaimUnderRedoReview(return_: {
  reviewingParty?: ReviewingParty | null;
  status?: ReturnStatus;
}): boolean {
  return (
    return_.reviewingParty === ReviewingParty.Redo &&
    (return_.status === ReturnStatus.NEEDS_REVIEW ||
      return_.status === ReturnStatus.IN_REVIEW)
  );
}
